import { ODataModel, ODataObject } from '../../interfaces/odata-model.interface';
import { JsonUtilities } from '../../utilities/json-utilities';
import { ArrayModelOptions, ArrayModelOptionsFromJson, JsonArrayModelOptions } from '../options/array-model-options';

export class ArrayModel<T extends ODataModel> implements ODataModel {
    public constructor(
        public name: string,
        public value: T[] = [],
        public options = new ArrayModelOptions(),
    ) {}

    /**
     * Iterate over internal models array and call outmodel conversion function for each of the models
     *
     * @returns {object} the outmodel representation of this array model
     */

    public getOutModel(): ODataArrayOutModel {
        const outModel: ODataArrayOutModel = {};
        outModel[this.name] = this.value.map((model) => model.getOutModel());

        return outModel;
    }

    /**
     * Add a single model to this array model
     *
     * @param model
     */
    public addModel(model: T): void {
        // check if the array already has reach max capacity
        if (this.value.length >= this.options.maxEntries) {
            throw new Error(`Could not add model to array: already at max capacity (${this.options.maxEntries})`);
        }

        this.value.push(model);
    }

    /**
     * Add a single model to this array model at an index
     *
     * @param {model} model
     * @param {number} index
     */
    public addModelAtIndex(model: T, index: number): void {
        // check if the array already has reach max capacity
        if (this.value.length >= this.options.maxEntries) {
            throw new Error(`Could not add model to array: already at max capacity (${this.options.maxEntries})`);
        }

        this.value.splice(index, 0, model);
    }

    /**
     * Copy an array of models to this array model
     *
     * @param models
     */
    public addModels(models: T[]): void {
        models.forEach((model) => this.addModel(model));
    }

    /**
     * Copy an array of models to this array model start from a given index
     *
     * @param models new models to be added
     * @param index index of the first model that must be replaced
     */
    public replaceModelAtIndex(models: T[], index: number): void {
        // check if the array crosses max capacity with new models
        if (this.value.length + models.length > this.options.maxEntries) {
            throw new Error(`Could not add models to array: would exceed max capacity (${this.options.maxEntries})`);
        }

        this.value.splice(index, 1, ...models);
    }

    /**
     * Remove a model contained in the array
     *
     * @param index
     * @returns the removed model
     */
    public removeModel(index: number): T {
        if (index >= this.value.length) {
            throw new Error(
                `Could not remove model from array: index '${index}' is larger than number of entries (${this.value.length})`,
            );
        }

        const removedModel = this.value[index];
        this.value.splice(index, 1);
        return removedModel;
    }

    public getModelByIndex(index: number): T {
        if (index >= this.value.length) {
            throw new Error(
                `Could not return model from array: index '${index}' is larger than number of entries (${this.value.length})`,
            );
        }

        return this.value[index];
    }
}

export class ArrayModelFromJson<T extends ODataModel> extends ArrayModel<T> {
    public constructor(type: new (...unusedTypeArgs: unknown[]) => T, data: JsonArrayModel, ...args: unknown[]) {
        super(
            data.name,
            data.value.map((entry) => {
                return JsonUtilities.fromJson<T>(type, entry, ...args);
            }),
            new ArrayModelOptionsFromJson(data.options),
        );
    }
}

export interface JsonArrayModel {
    name: string;
    value: unknown[];
    options: JsonArrayModelOptions;
}

// need here type because empty interfaces are not allowed
export type ODataArrayOutModel = ODataObject;
